import { useEffect, useRef, useState } from "react";
import { ArrowLeft, History, Trash2, Lock, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useVault } from "@/context/VaultContext";
import { db } from "@/lib/db";
import { saveDraft, stampVersion } from "@/lib/store";
import { summarize } from "@/lib/summarize";
import { creatorStamp } from "@/lib/watermark";
import { stamp } from "@/lib/util";
import { Attachments } from "./Attachments";
import { MetaBar } from "./MetaBar";
import { scanPatch, scanWithToast } from "@/lib/scan";
import { VersionDrawer } from "./VersionDrawer";
import { DeleteNoteDialog } from "./DeleteNoteDialog";

function SummaryBox({ draft }) {
  const s = summarize(draft.body, draft.ref);
  if (!s.sentence && !s.date && !s.ref) return null;
  return (
    <div className="rounded-lg border bg-muted/40 p-4 text-sm" data-testid="auto-summary">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest"><Sparkles className="h-3.5 w-3.5" />Quick Summary</p>
      {s.sentence && <p className="mt-2" data-testid="summary-sentence">{s.sentence}</p>}
      {(s.date || s.ref) && <p className="mt-1 text-xs text-muted-foreground">{s.date && <>Date: <span data-testid="summary-date">{s.date}</span></>}{s.date && s.ref && " • "}{s.ref && <>Ref: <span data-testid="summary-ref">{s.ref}</span></>}</p>}
    </div>
  );
}

export default function NoteEditor() {
  const { noteId, setNoteId, refresh, profile, folders, folderName, setFolderId } = useVault();
  const [draft, setDraft] = useState(null);
  const [hist, setHist] = useState(false);
  const [del, setDel] = useState(false);
  const [busy, setBusy] = useState(false);
  const dirty = useRef(false);

  useEffect(() => { db.get("notes", noteId).then(setDraft); }, [noteId]);
  useEffect(() => {
    if (!draft || !dirty.current) return;
    const t = setTimeout(async () => { dirty.current = false; await saveDraft(draft); refresh(); }, 600);
    return () => clearTimeout(t);
  }, [draft, refresh]);

  if (!draft) return null;
  const isAppt = draft.folderId === "appointments";
  const askDelete = () => { dirty.current = false; setDel(true); };
  const set = (patch) => { dirty.current = true; setDraft((d) => ({ ...d, ...patch })); };
  const scanAttachment = async (f) => {
    const res = await scanWithToast(f.blob, { name: f.name, mime: f.mime });
    if (!res) return;
    dirty.current = true;
    setDraft((d) => ({ ...d, ...scanPatch(d, res, f.name) }));
  };
  const seal = async () => {
    setBusy(true);
    dirty.current = false;
    const { version, note } = await stampVersion(draft, profile, folderName(draft.folderId));
    setDraft(note); await refresh(); setBusy(false);
    toast.success(`Saved & locked as Version ${version.n}`, { description: `${version.localTime} • ✓ Timestamped & Hash-Sealed` });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-4xl pb-28" data-testid="note-editor">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => setNoteId(null)} data-testid="back-to-folder-button"><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
        <Select value={draft.folderId} onValueChange={(v) => { set({ folderId: v }); setFolderId(v); }}>
          <SelectTrigger className="w-auto max-w-[240px] h-9" data-testid="note-folder-select"><SelectValue /></SelectTrigger>
          <SelectContent>{folders.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="ghost" size="icon" onClick={askDelete} className="ml-auto order-last text-destructive" aria-label="Delete note" data-testid="delete-note-top-button"><Trash2 className="h-4 w-4" /></Button>
        <Badge variant="outline" className="gap-1 font-mono" data-testid="note-version-badge"><Lock className="h-3 w-3 text-primary" />{draft.versionCount ? `Saved (v${draft.versionCount})` : "Draft"}</Badge>
      </div>
      <Input value={draft.title} onChange={(e) => set({ title: e.target.value })} placeholder={isAppt ? "Appointment Title" : "Note title"}
        className="mt-6 h-auto border-0 border-b rounded-none px-0 font-heading text-2xl md:text-3xl font-bold tracking-tight focus-visible:ring-0 bg-transparent" data-testid="note-title-input" />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 font-mono text-[11px] tracking-wider text-muted-foreground">
        <span>Created {stamp(new Date(draft.createdAt))}</span>
        {draft.lastHash && <Badge variant="outline" className="gap-1 font-sans text-[11px] tracking-normal" data-testid="note-verified-badge"><Check className="h-3 w-3" />Timestamped & Hash-Sealed</Badge>}
      </div>
      <div className="mt-5 space-y-6">
        <MetaBar draft={draft} set={set} />
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-mono uppercase tracking-widest text-primary">{isAppt ? "Notes, preparation & talking points" : "Note"}</span>
          </div>
          <Textarea value={draft.body} onChange={(e) => set({ body: e.target.value })} rows={12} placeholder={isAppt ? "What to prepare, questions to ask, talking points, outcome…" : "Write your note…"} className="text-base leading-relaxed" data-testid="note-body-input" />
          {profile.apply.notes && creatorStamp(profile) && <p className="mt-2 text-xs font-mono text-muted-foreground" data-testid="note-creator-stamp">Creator Stamp: {creatorStamp(profile)}</p>}
        </div>
        <SummaryBox draft={draft} />
        <Attachments note={draft} onScan={scanAttachment} />
      </div>
      <div className="fixed bottom-0 inset-x-0 lg:left-72 z-20 border-t bg-background/85 backdrop-blur-md px-4 py-3 flex gap-2 justify-end">
        <Button variant="ghost" onClick={askDelete} className="text-destructive mr-auto" data-testid="delete-note-button"><Trash2 className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Delete</span></Button>
        <Button variant="outline" onClick={() => setHist(true)} data-testid="version-history-button"><History className="h-4 w-4 mr-1" />Versions</Button>
        <Button onClick={seal} disabled={busy} data-testid="save-stamp-button"><Lock className="h-4 w-4 mr-1" />Save & Lock Note</Button>
      </div>
      <VersionDrawer note={draft} open={hist} onOpenChange={setHist}
        onRestore={(patch, n) => { set(patch); toast.success(`Version ${n} loaded into editor`, { description: "Edit, then Save & Lock to create a new version." }); }} />
      <DeleteNoteDialog note={draft} open={del} onOpenChange={setDel} />
    </div>
  );
}
